// front-end/src/features/pos/stores/shiftSessionStore.js

import { create } from 'zustand';
import api from '../../../api/axiosConfig';

const TERMINAL_KEY = 'ventahub_terminal_id';

export const useShiftSessionStore = create((set, get) => ({
  // ── State ─────────────────────────────────────────────────────────────────
  activeSession: null,

  // 'checking'  — verifying with server on POS load
  // 'resuming'  — open session found, cashier must decide: continue or terminate
  // 'open'      — session active, POS is rendered
  // 'none'      — no open session, show "Start New Shift" form
  sessionStatus: 'checking',

  // Device identifier — read from localStorage, persists across browser sessions
  // so the same PC always maps to the same terminalId.
  terminalId: null,

  // ── Actions ───────────────────────────────────────────────────────────────

  /**
   * Read terminalId from localStorage. Generate and persist one if missing.
   * Called once on POS entry — safe to call multiple times.
   */
  initTerminalId() {
    let tid = localStorage.getItem(TERMINAL_KEY);
    if (!tid) {
      tid = `POS-${Date.now()}`;
      localStorage.setItem(TERMINAL_KEY, tid);
    }
    set({ terminalId: tid });
    return tid;
  },

  /**
   * Ask the server whether an open PosShiftSession exists for this terminal.
   * Transitions sessionStatus to 'resuming' (found) or 'none' (not found).
   * On any network error: defaults to 'none' so the POS is never blocked.
   */
  async checkActiveSession() {
    const { terminalId } = get();
    if (!terminalId) {
      set({ sessionStatus: 'none' });
      return;
    }
    set({ sessionStatus: 'checking' });
    try {
      const { data } = await api.get('/pos/shift-session/active', {
        params: { terminalId },
      });
      if (data.session) {
        set({ activeSession: data.session, sessionStatus: 'resuming' });
      } else {
        set({ sessionStatus: 'none' });
      }
    } catch {
      // Network unavailable or auth error — degrade gracefully to 'none'
      // so the cashier can still open a new shift.
      set({ sessionStatus: 'none' });
    }
  },

  /** Called after a successful open or after the cashier chooses "Continue". */
  setActiveSession(session) {
    set({ activeSession: session, sessionStatus: 'open' });
  },

  /** Called after a successful close. Resets to 'none' for the next cashier. */
  clearSession() {
    set({ activeSession: null, sessionStatus: 'none' });
  },
}));
